import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart3, Calendar, LogOut, RefreshCw, ExternalLink, Upload, Filter, FileSpreadsheet, ChevronLeft, ChevronDown, ChevronUp, Package, Users, Eye, Clock, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { PrimaryButton, SecondaryButton } from './buttons';
import { DayColumn } from '@dexco/sales-schedule-chart';
import { parseCsvToDayColumns } from '../utils/parseCsv';
import { getAvailableCharts, GeneratedChart, formatChartDate, formatStatus, getAllocatedOrderStatus, AllocatedOrder, type OrderFilters } from '../utils/vendorApi';
import { toast } from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { NotificationsBell } from './NotificationsBell';
import { AuthContext } from '../App';

interface VendorDashboardProps {
    onLogout: () => void;
    onViewChart?: (schedule: DayColumn[]) => void; // Now optional since we'll use routing
}

interface DateFilters {
    startDate: string;
}

// Component for order filters within dropdown
interface OrderFiltersProps {
    chartUuid: string;
    orders: AllocatedOrder[];
    userProfile: string;
    dropdownFilters: Record<string, { vendedor: string; cliente: string; statusProducao: string }>;
    setDropdownFilters: React.Dispatch<React.SetStateAction<Record<string, { vendedor: string; cliente: string; statusProducao: string }>>>;
    onFiltersApplied?: (chartUuid: string, filters: { vendedor: string; cliente: string; statusProducao: string }) => void;
}

const OrderFilters: React.FC<OrderFiltersProps> = ({
    chartUuid,
    orders,
    userProfile,
    dropdownFilters,
    setDropdownFilters,
    onFiltersApplied
}) => {
    // Estados separados para inputs (draft) e filtros ativos
    const [draftFilters, setDraftFilters] = useState({
        vendedor: '',
        cliente: '',
        statusProducao: ''
    });

    const [showVendedorDropdown, setShowVendedorDropdown] = useState(false);
    const [showClienteDropdown, setShowClienteDropdown] = useState(false);

    const vendedorRef = useRef<HTMLDivElement>(null);
    const clienteRef = useRef<HTMLDivElement>(null);

    // Get current ACTIVE filters for this chart
    const activeFilters = dropdownFilters[chartUuid] || { vendedor: '', cliente: '', statusProducao: '' };

    // Check if there are changes to apply
    const hasChanges = draftFilters.vendedor !== activeFilters.vendedor ||
        draftFilters.cliente !== activeFilters.cliente ||
        draftFilters.statusProducao !== activeFilters.statusProducao;

    // Debug states
    console.log('📋 [FILTER STATES]:', {
        chartUuid: chartUuid.slice(0, 8),
        activeFilters,
        draftFilters,
        hasChanges,
        hasActiveFilters: Object.values(activeFilters).some(v => v)
    });

    // Extract unique vendors and clients from orders - exact values only
    const vendedorOptions = [...new Set(
        orders
            .map(order => order.no_vendedor)
            .filter((vendedor): vendedor is string => Boolean(vendedor) && vendedor.trim() !== '')
            .map(vendedor => vendedor.trim()) // Normalize whitespace
    )].sort((a, b) => a.localeCompare(b, 'pt-BR'));

    const clienteOptions = [...new Set(
        orders
            .map(order => order.cli_razao_social)
            .filter((cliente): cliente is string => Boolean(cliente) && cliente.trim() !== '')
            .map(cliente => cliente.trim()) // Normalize whitespace
    )].sort((a, b) => a.localeCompare(b, 'pt-BR'));

    const statusOptions = [...new Set(
        orders
            .map(order => order.status_producao)
            .filter((status): status is string => typeof status === 'string' && status.trim() !== '')
            .map(status => status.trim())
    )].sort((a, b) => a.localeCompare(b, 'pt-BR'));

    // Filter options based on draft input
    const filteredVendedores = vendedorOptions.filter(vendedor =>
        vendedor.toLowerCase().includes(draftFilters.vendedor.toLowerCase())
    );

    const filteredClientes = clienteOptions.filter(cliente =>
        cliente.toLowerCase().includes(draftFilters.cliente.toLowerCase())
    );

    // Sync draft filters with active filters when active filters change externally (like clear all)
    useEffect(() => {
        setDraftFilters({
            vendedor: activeFilters.vendedor,
            cliente: activeFilters.cliente,
            statusProducao: activeFilters.statusProducao
        });
    }, [activeFilters.vendedor, activeFilters.cliente, activeFilters.statusProducao]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (vendedorRef.current && !vendedorRef.current.contains(event.target as Node)) {
                setShowVendedorDropdown(false);
            }
            if (clienteRef.current && !clienteRef.current.contains(event.target as Node)) {
                setShowClienteDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Funções para atualizar APENAS os draft filters (não aplica imediatamente)
    const handleVendedorSelect = (vendedor: string) => {
        setDraftFilters(prev => ({ ...prev, vendedor }));
        setShowVendedorDropdown(false);
    };

    const handleClienteSelect = (cliente: string) => {
        setDraftFilters(prev => ({ ...prev, cliente }));
        setShowClienteDropdown(false);
    };

    const handleStatusChange = (status: string) => {
        setDraftFilters(prev => ({ ...prev, statusProducao: status }));
    };

    // Função para APLICAR os filtros - agora refetch do backend
    const applyFilters = () => {
        console.log('✅ [APPLYING FILTERS]:', {
            chartUuid: chartUuid.slice(0, 8),
            oldFilters: activeFilters,
            newFilters: draftFilters
        });

        // Update active filters state
        setDropdownFilters(prev => ({
            ...prev,
            [chartUuid]: { ...draftFilters }
        }));

        // Trigger refetch with new filters - call parent function
        if (typeof onFiltersApplied === 'function') {
            onFiltersApplied(chartUuid, draftFilters);
        }
    };

    // Função para limpar filtros
    const clearFilters = () => {
        const emptyFilters = { vendedor: '', cliente: '', statusProducao: '' };
        setDraftFilters(emptyFilters);
        setDropdownFilters(prev => ({
            ...prev,
            [chartUuid]: emptyFilters
        }));

        // Trigger refetch with empty filters
        if (typeof onFiltersApplied === 'function') {
            onFiltersApplied(chartUuid, emptyFilters);
        }
    };

    const hasActiveFilters = activeFilters.vendedor || activeFilters.cliente || activeFilters.statusProducao;

    return (
        <div className="mb-4 p-3 bg-gray-100 rounded-lg border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                <h4 className="text-sm font-medium text-gray-700">Filtrar Ordens</h4>
                {hasActiveFilters && (
                    <button
                        onClick={clearFilters}
                        className="text-xs text-gray-600 hover:text-gray-800 underline self-start sm:self-auto"
                    >
                        Limpar Filtros
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Vendedor Filter - Only for Directors */}
                {userProfile === 'diretor' && vendedorOptions.length > 0 && (
                    <div ref={vendedorRef}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Vendedor
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={draftFilters.vendedor}
                                onChange={(e) => {
                                    setDraftFilters(prev => ({ ...prev, vendedor: e.target.value }));
                                    setShowVendedorDropdown(true);
                                }}
                                onFocus={() => setShowVendedorDropdown(true)}
                                placeholder="Digite ou selecione vendedor"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        // Only accept exact matches or clear the filter
                                        const exact = vendedorOptions.find(v => v.toLowerCase() === draftFilters.vendedor.toLowerCase().trim());
                                        if (exact) {
                                            handleVendedorSelect(exact);
                                        } else {
                                            setDraftFilters(prev => ({ ...prev, vendedor: '' }));
                                        }
                                        setShowVendedorDropdown(false);
                                    }
                                }}
                            />
                            {showVendedorDropdown && filteredVendedores.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                                    {filteredVendedores.map((vendedor) => (
                                        <button
                                            key={vendedor}
                                            onClick={() => handleVendedorSelect(vendedor)}
                                            className="w-full px-2 py-1 text-left text-sm text-gray-900 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                                        >
                                            {vendedor}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Cliente Filter - For all users */}
                {clienteOptions.length > 0 && (
                    <div ref={clienteRef}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Cliente
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={draftFilters.cliente}
                                onChange={(e) => {
                                    setDraftFilters(prev => ({ ...prev, cliente: e.target.value }));
                                    setShowClienteDropdown(true);
                                }}
                                onFocus={() => setShowClienteDropdown(true)}
                                placeholder="Digite ou selecione cliente"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-transparent"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        // Only accept exact matches or clear the filter
                                        const exact = clienteOptions.find(c => c.toLowerCase() === draftFilters.cliente.toLowerCase().trim());
                                        if (exact) {
                                            handleClienteSelect(exact);
                                        } else {
                                            setDraftFilters(prev => ({ ...prev, cliente: '' }));
                                        }
                                        setShowClienteDropdown(false);
                                    }
                                }}
                            />
                            {showClienteDropdown && filteredClientes.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                                    {filteredClientes.map((cliente) => (
                                        <button
                                            key={cliente}
                                            onClick={() => handleClienteSelect(cliente)}
                                            className="w-full px-2 py-1 text-left text-sm text-gray-900 hover:bg-green-50 focus:bg-green-50 focus:outline-none truncate"
                                        >
                                            {cliente}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {statusOptions.length > 0 && (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Status de Produção
                        </label>
                        <select
                            value={draftFilters.statusProducao}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                        >
                            <option value="">Todos</option>
                            {statusOptions.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Botões de Ação */}
            <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-200">
                <div className="flex gap-2">
                    {/* Botão Aplicar Filtros */}
                    <button
                        onClick={applyFilters}
                        disabled={!hasChanges}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${hasChanges
                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                    >
                        Aplicar Filtros
                        {hasChanges && (
                            <span className="ml-1 w-2 h-2 bg-orange-400 rounded-full inline-block" />
                        )}
                    </button>

                    {/* Botão Limpar */}
                    {hasActiveFilters && (
                        <button
                            onClick={clearFilters}
                            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                        >
                            Limpar Filtros
                        </button>
                    )}
                </div>
            </div>

            {/* Active Filters - Apenas mostra filtros que estão ATIVAMENTE aplicados */}
            {hasActiveFilters && (
                <div className="flex flex-wrap gap-2 mt-3">
                    {activeFilters.vendedor && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            👤 {activeFilters.vendedor}
                        </span>
                    )}
                    {activeFilters.cliente && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            🏢 {activeFilters.cliente.length > 25 ? `${activeFilters.cliente.substring(0, 25)}...` : activeFilters.cliente}
                        </span>
                    )}
                    {activeFilters.statusProducao && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                            🏭 {activeFilters.statusProducao}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

// Component for filtered orders list
interface FilteredOrdersListProps {
    chartUuid: string;
    orders: AllocatedOrder[];
    dropdownFilters: Record<string, { vendedor: string; cliente: string; statusProducao: string }>;
    userProfile: string;

    openOrderModal: (order: AllocatedOrder) => void;
    getStatusColor: (status?: string) => string;
}

const FilteredOrdersList: React.FC<FilteredOrdersListProps> = ({
    chartUuid,
    orders,
    dropdownFilters,
    userProfile,
    openOrderModal,
    getStatusColor
}) => {
    const activeFilters = dropdownFilters[chartUuid] || { vendedor: '', cliente: '', statusProducao: '' };

    console.log('📄 [ORDERS FROM BACKEND]:', {
        chartUuid: chartUuid.slice(0, 8),
        activeFilters,
        totalOrders: orders.length,
        message: 'Orders already filtered by backend - no frontend filtering needed'
    });

    // Ordens já vêm filtradas do backend - não precisamos filtrar aqui!
    const displayOrders = orders;

    const hasActiveFilters = Object.values(activeFilters).some(v => v);

    if (displayOrders.length === 0) {
        return (
            <div className="text-center py-8">
                <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                    {hasActiveFilters
                        ? 'Nenhuma ordem encontrada com os filtros aplicados'
                        : 'Nenhuma ordem encontrada nesta otimização'
                    }
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3 max-h-96 overflow-y-auto">
            {displayOrders.map((order) => (
                <div
                    key={order.ordem || order.allocated_order_id}
                    className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                    {/* Header com Ordem e Cliente */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 gap-2">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h4 className="font-semibold text-gray-900 text-sm">
                                    Ordem #{order.ordem}
                                </h4>
                                <span className="text-xs text-gray-500">
                                    Item {order.item}
                                </span>
                            </div>
                            <p className="text-sm text-gray-700 break-words min-w-0 flex-shrink-0">
                                {order.cli_razao_social}
                            </p>
                            {userProfile === 'diretor' && (
                                <div className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                                    <Users className="w-3 h-3" />
                                    <span className="break-words">{order.no_vendedor}</span>
                                </div>
                            )}
                        </div>
                        <SecondaryButton
                            onClick={() => openOrderModal(order)}
                            leftIcon={<Eye className="w-3 h-3" />}
                            className="text-xs px-2 py-1 shrink-0 self-start sm:self-auto w-full sm:w-auto justify-center sm:justify-start"
                        >
                            Ver Detalhes
                        </SecondaryButton>
                    </div>

                    {/* Grid de Informações Principais */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                        {/* Dias para Produção */}
                        {order.dias_para_producao !== undefined && (
                            <div className="flex items-center gap-2 min-w-0">
                                <Clock className="w-4 h-4 text-blue-600 shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-xs text-gray-600">Produção</div>
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                        {order.dias_para_producao} dias
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Dimensões */}
                        {order.dimensoes_resumo && (
                            <div className="flex items-center gap-2 min-w-0">
                                <Package className="w-4 h-4 text-purple-600 shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-xs text-gray-600">Dimensões</div>
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                        {order.dimensoes_resumo}mm
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Dias de Atraso */}
                        {order.dias_atraso !== undefined && (
                            <div className="flex items-center gap-2 min-w-0">
                                {order.dias_atraso > 0 ? (
                                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                                ) : (
                                    <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                                )}
                                <div className="min-w-0">
                                    <div className="text-xs text-gray-600">Atraso</div>
                                    <div className={`text-sm font-medium truncate ${order.dias_atraso > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {order.dias_atraso === 0 ? 'No prazo' : `${order.dias_atraso} dias`}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Status de Produção */}
                        {order.status_producao && (
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                    <div className="w-2 h-2 bg-blue-600 rounded-full" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-xs text-gray-600">Produção</div>
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                        {order.status_producao}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Status Pills */}
                    <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                            {order.status}
                        </span>
                        {order.prazo_status && (
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(order.prazo_status)}`}>
                                {order.prazo_status}
                            </span>
                        )}
                        {order.volume_categoria && (
                            <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full font-medium">
                                Volume: {order.volume_categoria}
                            </span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export const VendorDashboard: React.FC<VendorDashboardProps> = ({
    onLogout,
    onViewChart,
}) => {
    const navigate = useNavigate();
    const { userProfile, userInfo } = useContext(AuthContext);

    // Show back button if accessed by director
    const shouldShowBackButton = userProfile === 'diretor';

    const [charts, setCharts] = useState<GeneratedChart[]>([]);
    const [filteredCharts, setFilteredCharts] = useState<GeneratedChart[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [logoError, setLogoError] = useState(false);
    const [filters, setFilters] = useState<DateFilters>({
        startDate: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [visibleCount, setVisibleCount] = useState(10);
    const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

    // Allocated orders state
    const [allocatedOrders, setAllocatedOrders] = useState<Record<string, AllocatedOrder[]>>({});
    const [loadingOrders, setLoadingOrders] = useState<Record<string, boolean>>({});
    const [expandedCharts, setExpandedCharts] = useState<Record<string, boolean>>({});

    // Order details modal state
    const [selectedOrder, setSelectedOrder] = useState<AllocatedOrder | null>(null);
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);

    // Dropdown filters state (per chart)
    const [dropdownFilters, setDropdownFilters] = useState<Record<string, { vendedor: string; cliente: string; statusProducao: string }>>({});



    // Load charts from API
    useEffect(() => {
        const loadCharts = async () => {
            setIsLoading(true);
            try {
                const response = await getAvailableCharts();

                if (response.success && response.data) {
                    // Sort by newest first when initially loading
                    const sortedData = [...response.data].sort((a, b) => {
                        const dateA = new Date(a.created_at);
                        const dateB = new Date(b.created_at);
                        return dateB.getTime() - dateA.getTime(); // Most recent first
                    });

                    setCharts(sortedData);
                    setFilteredCharts(sortedData);


                } else {
                    throw new Error(response.error || 'Erro desconhecido');
                }
            } catch (error) {
                console.error('Erro ao carregar gráficos:', error);
                toast.error('Erro ao carregar lista de gráficos');
            } finally {
                setIsLoading(false);
            }
        };

        loadCharts();
    }, []);



    // Apply filters and sorting when filters or sortOrder change
    useEffect(() => {
        let filtered = charts;

        // Apply date filter
        if (filters.startDate) {
            filtered = charts.filter(chart => {
                const chartDate = new Date(chart.created_at);
                const chartDateStr = chartDate.toISOString().split('T')[0]; // YYYY-MM-DD format
                return chartDateStr >= filters.startDate;
            });
        }

        // Apply sorting
        filtered.sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);

            if (sortOrder === 'newest') {
                return dateB.getTime() - dateA.getTime(); // Most recent first
            } else {
                return dateA.getTime() - dateB.getTime(); // Oldest first
            }
        });

        setFilteredCharts(filtered);
        setVisibleCount(10); // Reset pagination when filters change
    }, [charts, filters, sortOrder]);

    const handleViewChart = async (uuid: string) => {
        // Navigate directly to cronograma with the chart UUID as taskId
        navigate(`/cronograma/${uuid}`);
    };

    // Keep the CSV upload functionality for manual uploads
    const handleCsvViewChart = async (scheduleData: DayColumn[]) => {
        try {
            if (onViewChart) {
                // Use legacy callback if provided
                onViewChart(scheduleData);
            } else {
                // Save to sessionStorage and open in new tab (fallback)
                sessionStorage.setItem('dexco-schedule-data', JSON.stringify(scheduleData));

                // Get task_id from sessionStorage if available
                const taskId = sessionStorage.getItem('dexco-task-id');

                // Use unified route with task_id if available, otherwise fallback to old route
                let scheduleUrl: string;
                if (taskId) {
                    scheduleUrl = `${window.location.origin}/cronograma/${taskId}`;
                } else {
                    scheduleUrl = `${window.location.origin}/schedule`;
                }

                const newTab = window.open(scheduleUrl, '_blank');

                if (!newTab) {
                    toast.error('Pop-up bloqueado. Permita pop-ups para visualizar o cronograma.');
                }
            }
            toast.success('Cronograma carregado com sucesso!');
        } catch (error) {
            console.error('Erro ao carregar cronograma:', error);
            toast.error('Erro ao carregar cronograma');
        }
    };

    const refreshCharts = async () => {
        setIsLoading(true);
        try {
            const response = await getAvailableCharts();

            if (response.success && response.data) {
                // Sort by newest first when refreshing
                const sortedData = [...response.data].sort((a, b) => {
                    const dateA = new Date(a.created_at);
                    const dateB = new Date(b.created_at);
                    return dateB.getTime() - dateA.getTime(); // Most recent first
                });

                setCharts(sortedData);
                toast.success('Lista atualizada!');
            } else {
                throw new Error(response.error || 'Erro ao atualizar');
            }
        } catch (error) {
            console.error('Erro ao atualizar gráficos:', error);
            toast.error('Erro ao atualizar lista de gráficos');
        } finally {
            setIsLoading(false);
        }
    };

    const clearFilters = () => {
        setFilters({
            startDate: ''
        });
    };

    // Function to fetch allocated orders for a specific optimization
    const fetchAllocatedOrders = async (uuid: string, filters?: { vendedor: string; cliente: string; statusProducao: string }) => {
        setLoadingOrders(prev => ({ ...prev, [uuid]: true }));

        try {
            // Determine if we should filter by vendor name based on user type
            const vendorName = userProfile === 'vendedor' && userInfo?.full_name
                ? userInfo.full_name
                : undefined;

            // Convert UI filters to API format
            const apiFilters: OrderFilters | undefined = filters && Object.values(filters).some(v => v) ? {
                vendedor: filters.vendedor || undefined,
                cliente: filters.cliente || undefined,
                status: filters.statusProducao || undefined
            } : undefined;

            console.log('🔄 [FETCH WITH FILTERS]:', {
                uuid: uuid.slice(0, 8),
                vendorName,
                uiFilters: filters,
                apiFilters
            });

            const response = await getAllocatedOrderStatus(uuid, vendorName, apiFilters);

            if (response.success && response.data) {
                setAllocatedOrders(prev => ({
                    ...prev,
                    [uuid]: response.data || []
                }));

                console.log(`📊 [ALLOCATED ORDERS] Loaded ${response.data.length} orders for ${uuid.slice(0, 8)} with filters:`, apiFilters);
            } else {
                throw new Error(response.error || 'Erro ao buscar ordens');
            }
        } catch (error) {
            console.error('Error fetching allocated orders:', error);
            toast.error(`Erro ao carregar status das ordens para ${uuid.slice(0, 8).toUpperCase()}`);
        } finally {
            setLoadingOrders(prev => ({ ...prev, [uuid]: false }));
        }
    };

    // Function to toggle chart expansion and fetch orders if not already loaded
    const toggleChartExpansion = async (uuid: string) => {
        const isExpanding = !expandedCharts[uuid];

        setExpandedCharts(prev => ({
            ...prev,
            [uuid]: isExpanding
        }));

        // Fetch orders if expanding and not already loaded
        if (isExpanding && !allocatedOrders[uuid]) {
            await fetchAllocatedOrders(uuid);
        }
    };

    // Functions to handle order details modal
    const openOrderModal = (order: AllocatedOrder) => {
        setSelectedOrder(order);
        setIsOrderModalOpen(true);
    };

    const closeOrderModal = () => {
        setSelectedOrder(null);
        setIsOrderModalOpen(false);
    };

    // Helper function to get status color based on prazo_status
    const getStatusColor = (prazoStatus?: string) => {
        switch (prazoStatus?.toLowerCase()) {
            case 'no prazo':
                return 'bg-green-100 text-green-800';
            case 'atrasado':
                return 'bg-red-100 text-red-800';
            case 'em risco':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };



    // CSV Upload handling
    const onDrop = async (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.csv')) {
            toast.error('Por favor, selecione apenas arquivos CSV');
            return;
        }

        try {
            const text = await file.text();
            const schedule = parseCsvToDayColumns(text);

            if (schedule.length === 0) {
                throw new Error('Nenhum dado válido encontrado no CSV');
            }

            await handleCsvViewChart(schedule);
        } catch (error) {
            console.error('Erro ao processar CSV:', error);
            toast.error('Erro ao processar arquivo CSV');
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv']
        },
        multiple: false
    });

    return (
        <div className="min-h-screen bg-dexco-gray-bg">
            <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
                        {/* Dexco Logo */}
                        <div className="flex items-center shrink-0">
                            {!logoError ? (
                                <img
                                    src="/logo-ico.svg"
                                    alt="Dexco"
                                    className="h-7 w-7 sm:h-8 sm:w-8"
                                    onError={() => setLogoError(true)}
                                />
                            ) : (
                                <div className="text-lg sm:text-xl font-display font-bold text-dexco-green-primary">
                                    DEXCO
                                </div>
                            )}
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-lg sm:text-xl font-display font-bold text-dexco-text-dark">
                                Cronogramas Disponíveis
                            </h1>
                            <div className="space-y-1">
                                <p className="text-xs sm:text-sm text-gray-600">
                                    <span className="font-medium">Perfil:</span> {userProfile === 'diretor' ? 'Diretor' : 'Vendedor'}
                                </p>
                                {userInfo && (
                                    <>
                                        <p className="text-xs sm:text-sm text-gray-600 truncate">
                                            <span className="font-medium">Nome:</span> {userInfo.full_name}
                                        </p>
                                        <p className="text-xs sm:text-sm text-gray-600 break-all">
                                            <span className="font-medium">Email:</span> {userInfo.email}
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 shrink-0">
                        <SecondaryButton
                            onClick={refreshCharts}
                            leftIcon={<RefreshCw className="w-4 h-4" />}
                            disabled={isLoading}
                            className="w-full sm:w-auto justify-center"
                        >
                            Atualizar
                        </SecondaryButton>

                        {/* Mobile: Single row with all action buttons */}
                        <div className="flex items-center gap-2 sm:gap-4 justify-between sm:justify-start">
                            <NotificationsBell />
                            {shouldShowBackButton && (
                                <SecondaryButton
                                    onClick={() => navigate('/home')}
                                    leftIcon={<ChevronLeft className="w-4 h-4" />}
                                    className="flex-1 sm:flex-none justify-center"
                                >
                                    <span className="hidden sm:inline">Voltar</span>
                                    <span className="sm:hidden">←</span>
                                </SecondaryButton>
                            )}
                            <SecondaryButton
                                onClick={onLogout}
                                leftIcon={<LogOut className="w-4 h-4" />}
                                className="flex-1 sm:flex-none justify-center"
                            >
                                <span className="hidden sm:inline">Sair</span>
                                <span className="sm:hidden">⏻</span>
                            </SecondaryButton>
                        </div>
                    </div>
                </div>

                {/* Filters Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-dexco-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Filter className="w-4 h-4 text-blue-600" />
                            </div>
                            <h2 className="font-display font-semibold text-base sm:text-lg text-dexco-text-dark">
                                Filtros
                            </h2>
                        </div>
                        <SecondaryButton
                            onClick={() => setShowFilters(!showFilters)}
                            leftIcon={<Filter className="w-4 h-4" />}
                            className="w-full sm:w-auto justify-center"
                        >
                            <span className="sm:hidden">{showFilters ? 'Ocultar' : 'Mostrar'}</span>
                            <span className="hidden sm:inline">{showFilters ? 'Ocultar' : 'Mostrar'} Filtros</span>
                        </SecondaryButton>
                    </div>

                    {showFilters && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="space-y-4"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Start Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        A partir de
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.startDate}
                                        onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Selecione uma data"
                                    />
                                </div>





                                {/* Sort Order */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Ordenar por
                                    </label>
                                    <select
                                        value={sortOrder}
                                        onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="newest">Mais recentes primeiro</option>
                                        <option value="oldest">Mais antigos primeiro</option>
                                    </select>
                                </div>
                            </div>

                            {/* Active Filters Summary */}
                            {filters.startDate && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <div className="flex flex-wrap gap-2 items-center mb-4">
                                        <span className="text-sm font-medium text-gray-700">Filtros ativos:</span>
                                        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
                                            📅 A partir de: {new Date(filters.startDate).toLocaleDateString('pt-BR')}
                                            <button
                                                onClick={() => setFilters(prev => ({ ...prev, startDate: '' }))}
                                                className="hover:text-blue-600"
                                                title="Remover filtro de data"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end">
                                <SecondaryButton onClick={clearFilters}>
                                    Limpar Filtros
                                </SecondaryButton>
                            </div>
                        </motion.div>
                    )}
                </motion.div>

                {/* Charts List */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-dexco-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6"
                >
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-dexco-green-primary rounded-lg flex items-center justify-center">
                            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="font-display font-semibold text-base sm:text-lg text-dexco-text-dark">
                                Gráficos Gerados pela Diretoria
                            </h2>
                            <p className="text-sm text-gray-600">
                                {filteredCharts.length} de {charts.length} cronogramas encontrados
                            </p>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="w-8 h-8 border-2 border-dexco-green-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                <p className="text-gray-600 font-body">Carregando cronogramas...</p>
                            </div>
                        </div>
                    ) : filteredCharts.length === 0 ? (
                        <div className="text-center py-12">
                            <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {charts.length === 0 ? 'Nenhum cronograma disponível' : 'Nenhum cronograma encontrado com os filtros aplicados'}
                            </h3>
                            <p className="text-gray-600 mb-4">
                                {charts.length === 0
                                    ? 'Aguarde a diretoria gerar novos cronogramas para visualização.'
                                    : 'Tente ajustar os filtros ou limpar para ver todos os cronogramas.'
                                }
                            </p>
                            <SecondaryButton
                                onClick={refreshCharts}
                                leftIcon={<RefreshCw className="w-4 h-4" />}
                            >
                                Verificar novamente
                            </SecondaryButton>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredCharts.slice(0, visibleCount).map((chart) => {
                                const statusInfo = formatStatus(chart.status);
                                const isCompleted = chart.status.toLowerCase() === 'completed';
                                const isExpanded = expandedCharts[chart.uuid];
                                const isLoadingOrders = loadingOrders[chart.uuid];
                                const chartOrders = allocatedOrders[chart.uuid] || [];

                                return (
                                    <motion.div
                                        key={chart.uuid}
                                        whileHover={{ scale: 1.01 }}
                                        className="border border-gray-200 rounded-lg hover:border-dexco-green-primary transition-all overflow-hidden"
                                    >
                                        {/* Main Chart Row */}
                                        <div className="p-3 sm:p-4 hover:bg-gray-50">
                                            {/* Desktop Layout */}
                                            <div className="hidden sm:flex items-center justify-between">
                                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isCompleted ? 'bg-green-100' : 'bg-yellow-100'
                                                        }`}>
                                                        <Calendar className={`w-4 h-4 ${isCompleted ? 'text-green-600' : 'text-yellow-600'
                                                            }`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-dexco-text-dark truncate">
                                                            Cronograma #{chart.uuid.slice(0, 8).toUpperCase()}
                                                        </div>
                                                        <div className="text-sm text-gray-600 truncate">
                                                            Criado em: {formatChartDate(chart.created_at)}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            <span className={`text-xs font-medium ${statusInfo.color}`}>
                                                                {statusInfo.label}
                                                            </span>
                                                            {chart.completed_at && (
                                                                <span className="text-xs text-gray-500 truncate">
                                                                    • Concluído em: {formatChartDate(chart.completed_at)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2 shrink-0 ml-4">
                                                    {/* Orders Status Button */}
                                                    {isCompleted && (
                                                        <SecondaryButton
                                                            onClick={() => toggleChartExpansion(chart.uuid)}
                                                            leftIcon={isLoadingOrders ?
                                                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> :
                                                                <Package className="w-4 h-4" />
                                                            }
                                                            rightIcon={isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                            disabled={isLoadingOrders}
                                                            className="hidden md:flex"
                                                        >
                                                            {isLoadingOrders ? 'Carregando...' : 'Status das Ordens'}
                                                            {!isLoadingOrders && chartOrders.length > 0 && (
                                                                <span className="ml-1 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                                                                    {chartOrders.length}
                                                                </span>
                                                            )}
                                                        </SecondaryButton>
                                                    )}

                                                    {/* Compact Orders Status Button for Medium Screens */}
                                                    {isCompleted && (
                                                        <SecondaryButton
                                                            onClick={() => toggleChartExpansion(chart.uuid)}
                                                            leftIcon={isLoadingOrders ?
                                                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> :
                                                                <Package className="w-4 h-4" />
                                                            }
                                                            rightIcon={isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                            disabled={isLoadingOrders}
                                                            className="md:hidden"
                                                            title={isLoadingOrders ? 'Carregando...' : 'Status das Ordens'}
                                                        >
                                                            {!isLoadingOrders && chartOrders.length > 0 && (
                                                                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                                                                    {chartOrders.length}
                                                                </span>
                                                            )}
                                                        </SecondaryButton>
                                                    )}

                                                    {/* View Chart Button */}
                                                    <PrimaryButton
                                                        onClick={() => handleViewChart(chart.uuid)}
                                                        disabled={!isCompleted}
                                                        leftIcon={<ExternalLink className="w-4 h-4" />}
                                                    >
                                                        {!isCompleted ? 'Aguardando' : 'Visualizar'}
                                                    </PrimaryButton>
                                                </div>
                                            </div>

                                            {/* Mobile Layout */}
                                            <div className="sm:hidden space-y-3">
                                                {/* Header with Icon and Status */}
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isCompleted ? 'bg-green-100' : 'bg-yellow-100'
                                                        }`}>
                                                        <Calendar className={`w-4 h-4 ${isCompleted ? 'text-green-600' : 'text-yellow-600'
                                                            }`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-dexco-text-dark">
                                                            Cronograma #{chart.uuid.slice(0, 8).toUpperCase()}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            {formatChartDate(chart.created_at)}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            <span className={`text-xs font-medium px-2 py-1 rounded ${statusInfo.color}`}>
                                                                {statusInfo.label}
                                                            </span>
                                                            {chart.completed_at && (
                                                                <span className="text-xs text-gray-500">
                                                                    ✓ {formatChartDate(chart.completed_at)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Mobile Action Buttons */}
                                                <div className="flex flex-col gap-2">
                                                    {/* Primary Action */}
                                                    <PrimaryButton
                                                        onClick={() => handleViewChart(chart.uuid)}
                                                        disabled={!isCompleted}
                                                        leftIcon={<ExternalLink className="w-4 h-4" />}
                                                        className="w-full justify-center"
                                                    >
                                                        {!isCompleted ? 'Aguardando' : 'Visualizar Cronograma'}
                                                    </PrimaryButton>

                                                    {/* Secondary Action */}
                                                    {isCompleted && (
                                                        <SecondaryButton
                                                            onClick={() => toggleChartExpansion(chart.uuid)}
                                                            leftIcon={isLoadingOrders ?
                                                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> :
                                                                <Package className="w-4 h-4" />
                                                            }
                                                            rightIcon={isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                            disabled={isLoadingOrders}
                                                            className="w-full justify-center"
                                                        >
                                                            {isLoadingOrders ? 'Carregando...' : 'Status das Ordens'}
                                                            {!isLoadingOrders && chartOrders.length > 0 && (
                                                                <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                                                                    {chartOrders.length}
                                                                </span>
                                                            )}
                                                        </SecondaryButton>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Expanded Orders Section */}
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="border-t border-gray-200 bg-gray-50"
                                            >
                                                <div className="p-4">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <Package className="w-4 h-4 text-gray-600" />
                                                        <h3 className="font-medium text-gray-900">
                                                            Status das Ordens Alocadas
                                                        </h3>
                                                        {userProfile === 'vendedor' && userInfo && (
                                                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                                Filtrado para: {userInfo.full_name}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {isLoadingOrders ? (
                                                        <div className="flex items-center justify-center py-8">
                                                            <div className="text-center">
                                                                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                                                                <p className="text-sm text-gray-600">Carregando ordens...</p>
                                                            </div>
                                                        </div>
                                                    ) : chartOrders.length === 0 ? (
                                                        <div className="text-center py-8">
                                                            <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                                            <p className="text-sm text-gray-600">
                                                                {userProfile === 'vendedor'
                                                                    ? 'Nenhuma ordem encontrada para você nesta otimização'
                                                                    : 'Nenhuma ordem encontrada nesta otimização'
                                                                }
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {/* Filters for Orders */}
                                                            <OrderFilters
                                                                chartUuid={chart.uuid}
                                                                orders={chartOrders}
                                                                userProfile={userProfile || ''}
                                                                dropdownFilters={dropdownFilters}
                                                                setDropdownFilters={setDropdownFilters}
                                                                onFiltersApplied={(uuid, filters) => fetchAllocatedOrders(uuid, filters)}
                                                            />

                                                            {/* Filtered Orders List */}
                                                            <FilteredOrdersList
                                                                chartUuid={chart.uuid}
                                                                orders={chartOrders}
                                                                dropdownFilters={dropdownFilters}
                                                                userProfile={userProfile || ''}

                                                                openOrderModal={openOrderModal}
                                                                getStatusColor={getStatusColor}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                );
                            })}

                            {/* Load More Button */}
                            {visibleCount < filteredCharts.length && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-center pt-4"
                                >
                                    <SecondaryButton
                                        onClick={() => setVisibleCount(prev => Math.min(prev + 10, filteredCharts.length))}
                                        className="px-6 py-2"
                                    >
                                        Carregar mais {Math.min(10, filteredCharts.length - visibleCount)} cronogramas
                                    </SecondaryButton>
                                </motion.div>
                            )}

                            {/* Show total count */}
                            {filteredCharts.length > 0 && (
                                <div className="text-center pt-4 text-sm text-gray-500">
                                    Mostrando {Math.min(visibleCount, filteredCharts.length)} de {filteredCharts.length} cronogramas
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>

                {/* Manual CSV Upload Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-dexco-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6"
                >
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                        </div>
                        <div>
                            <h2 className="font-display font-semibold text-base sm:text-lg text-dexco-text-dark">
                                Importar CSV Manualmente
                            </h2>
                            <p className="text-xs sm:text-sm text-gray-600">
                                Arraste um arquivo CSV ou clique para selecionar
                            </p>
                        </div>
                    </div>

                    <div
                        {...getRootProps()}
                        className={`border-2 border-dashed rounded-lg p-4 sm:p-8 text-center transition-all cursor-pointer ${isDragActive
                            ? 'border-purple-400 bg-purple-50'
                            : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
                            }`}
                    >
                        <input {...getInputProps()} />
                        <div className="flex flex-col items-center gap-3 sm:gap-4">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                <FileSpreadsheet className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                            </div>
                            <div className="text-center">
                                <p className="text-base sm:text-lg font-medium text-dexco-text-dark mb-2">
                                    {isDragActive ? 'Solte o arquivo aqui' : 'Arraste um CSV ou clique para selecionar'}
                                </p>
                                <p className="text-xs sm:text-sm text-gray-600">
                                    Suporte para arquivos CSV com dados de cronograma
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Footer Info */}
                {filteredCharts.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg"
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                                <div className="w-2 h-2 bg-blue-600 rounded-full" />
                            </div>
                            <div className="text-sm text-blue-800">
                                <p className="font-medium mb-1">Como usar:</p>
                                <p>
                                    Use os filtros para encontrar cronogramas específicos ou importe um CSV manualmente.
                                    Clique em "Visualizar" para abrir o cronograma em uma nova janela.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Order Details Modal */}
            {isOrderModalOpen && selectedOrder && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={closeOrderModal}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Detalhes da Ordem #{selectedOrder.ordem}
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {selectedOrder.cli_razao_social}
                                </p>
                            </div>
                            <SecondaryButton
                                onClick={closeOrderModal}
                                leftIcon={<X className="w-4 h-4" />}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                Fechar
                            </SecondaryButton>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 space-y-6">
                            {/* Informações Básicas */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <Package className="w-5 h-5 text-blue-600" />
                                    Informações Básicas
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Ordem</label>
                                            <p className="text-sm text-gray-900">#{selectedOrder.ordem}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Item</label>
                                            <p className="text-sm text-gray-900">{selectedOrder.item}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Cliente</label>
                                            <p className="text-sm text-gray-900">{selectedOrder.cli_razao_social}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Vendedor</label>
                                            <p className="text-sm text-gray-900">{selectedOrder.no_vendedor}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        {selectedOrder.qt_vol_m3_liquido_gerencial && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Volume (m³)</label>
                                                <p className="text-sm text-gray-900">{selectedOrder.qt_vol_m3_liquido_gerencial.toFixed(2)}</p>
                                            </div>
                                        )}
                                        {selectedOrder.volume_categoria && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Categoria de Volume</label>
                                                <p className="text-sm text-gray-900">{selectedOrder.volume_categoria}</p>
                                            </div>
                                        )}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Status</label>
                                            <span className="inline-block text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                                                {selectedOrder.status}
                                            </span>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Confirmado</label>
                                            <p className="text-sm text-gray-900">
                                                {selectedOrder.prd_confirmed ? 'Sim' : 'Não'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Dimensões Técnicas */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <Package className="w-5 h-5 text-purple-600" />
                                    Dimensões Técnicas
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        {selectedOrder.prd_espessura && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Espessura</label>
                                                <p className="text-sm text-gray-900">{selectedOrder.prd_espessura}mm</p>
                                            </div>
                                        )}
                                        {selectedOrder.prd_largura && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Largura</label>
                                                <p className="text-sm text-gray-900">{selectedOrder.prd_largura}mm</p>
                                            </div>
                                        )}
                                        {selectedOrder.prd_comprimento && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Comprimento</label>
                                                <p className="text-sm text-gray-900">{selectedOrder.prd_comprimento}mm</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        {selectedOrder.dimensoes_resumo && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Resumo das Dimensões</label>
                                                <p className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">
                                                    {selectedOrder.dimensoes_resumo}mm
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Status e Prazos */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-green-600" />
                                    Status e Prazos
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        {selectedOrder.dias_para_producao !== undefined && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Dias para Produção</label>
                                                <p className="text-sm text-gray-900">{selectedOrder.dias_para_producao} dias</p>
                                            </div>
                                        )}
                                        {selectedOrder.dias_atraso !== undefined && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Dias de Atraso</label>
                                                <p className={`text-sm font-medium ${selectedOrder.dias_atraso > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    {selectedOrder.dias_atraso === 0 ? 'No prazo' : `${selectedOrder.dias_atraso} dias`}
                                                </p>
                                            </div>
                                        )}
                                        {selectedOrder.status_producao && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Status de Produção</label>
                                                <p className="text-sm text-gray-900">{selectedOrder.status_producao}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        {selectedOrder.prazo_status && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Status do Prazo</label>
                                                <span className={`inline-block text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(selectedOrder.prazo_status)}`}>
                                                    {selectedOrder.prazo_status}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Datas */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-orange-600" />
                                    Datas Importantes
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        {selectedOrder.dt_emissao && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Data de Emissão</label>
                                                <p className="text-sm text-gray-900">{new Date(selectedOrder.dt_emissao).toLocaleDateString('pt-BR')}</p>
                                            </div>
                                        )}
                                        {selectedOrder.dt_preparacao_material && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Preparação do Material</label>
                                                <p className="text-sm text-gray-900">{new Date(selectedOrder.dt_preparacao_material).toLocaleDateString('pt-BR')}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        {selectedOrder.dt_producao && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Data de Produção</label>
                                                <p className="text-sm text-gray-900">{new Date(selectedOrder.dt_producao).toLocaleDateString('pt-BR')}</p>
                                            </div>
                                        )}
                                        {selectedOrder.dt_criacao_remessa && (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Criação da Remessa</label>
                                                <p className="text-sm text-gray-900">{new Date(selectedOrder.dt_criacao_remessa).toLocaleDateString('pt-BR')}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end p-6 border-t border-gray-200">
                            <SecondaryButton onClick={closeOrderModal}>
                                Fechar
                            </SecondaryButton>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
}; 